<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Calendar from 'primevue/calendar';
import { useDateTime } from '@/composables';
import { StudentTotalReportService } from '@/services';
import StudentTotalReportDisciplines from './StudentTotalReportDisciplines.vue';

const $dateTime = useDateTime();

const filterDate = ref();

const minDate = new Date('2023-12-11 00:00:00');

const maxDate = ref();

const totals = ref({
  total_students_ytd_minutes: 0,
  total_students_all_time_minutes: 0,
  total_students_minutes_ytd_minutes: 0,
  total_students_minutes_all_time_minutes: 0,
  total_students_ytd_discipline: [],
  total_students_all_time_discipline: [],
});

onMounted(() => init());

const init = async () => {
  defineDate();
  getStudentTotals();
};

const defineDate = () => {
  filterDate.value = new Date();
  maxDate.value = new Date();
};

const getStudentTotals = async () => {
  const payload = { date: $dateTime.formatDateForApi(filterDate.value) };
  const response = await StudentTotalReportService.getStudentTotalReport(payload);
  totals.value = response.data.data;
};

const onDateSelect = (value) => {
  filterDate.value = value;
  getStudentTotals();
};

const formatNumber = (number: number) => new Intl.NumberFormat().format(Math.round(number));
</script>

<template>
  <div class="page-content">
    <Card>
      <template #content>
        <div class="flex flex-wrap gap-3">
          <div class="flex pt-4">
            <h2 v-text="'Student Total for '" />
          </div>
          <div class="flex">
            <Calendar
              v-model="filterDate"
              show-icon
              icon-display="input"
              class="mt-3 text-sm"
              date-format="MM, dd yy"
              :min-date="minDate"
              :max-date="maxDate"
              @date-select="onDateSelect"
            />
          </div>
        </div>
        <div class="w-full flex mt-2">
          <div class="w-full flex flex-wrap gap-3">
            <div class="flex-1">
              <div
                class="flex flex-wrap pt-3 pr-3 pl-3 pb-2 mt-3 mb-4 gap-3 border-2 border-gray-100 bg-white border-round-xl relative"
              >
                <div class="flex-1">
                  <div class="flex mb-2">
                    <Icon
                      name="perm_contact_calendar"
                      class="text-color-purple text-4xl p-2 bg-purple-light border-round align-self-start"
                    />
                    <div class="mx-3">
                      <h3 class="text-color-secondary text-base">Students, Year To Date</h3>
                      <p
                        class="text-color-purple-dark text-5xl font-bold"
                        v-text="formatNumber(totals.total_students_ytd_minutes)"
                      />
                    </div>
                    <Icon
                      v-tooltip.bottom="`Students served year to date for Sessions, Consultations, and Evaluations`"
                      name="info"
                      class="text-gray-400 text-base cursor-pointer align-self-start"
                    />
                  </div>
                </div>
                <div class="flex-1">
                  <div class="flex mb-2">
                    <Icon
                      name="assignment_turned_in"
                      class="text-color-pink text-4xl p-2 bg-pink-light border-round align-self-start"
                    />
                    <div class="mx-3">
                      <h3 class="text-color-secondary text-base">Students, Year To Date Minutes</h3>
                      <p
                        class="text-color-pink-dark text-5xl font-bold"
                        v-text="formatNumber(totals.total_students_minutes_ytd_minutes)"
                      />
                    </div>
                    <Icon
                      v-tooltip.bottom="`Student year to date minutes for Sessions, Consultations, and Evaluations`"
                      name="info"
                      class="text-gray-400 text-base cursor-pointer align-self-start"
                    />
                  </div>
                </div>
              </div>
              <StudentTotalReportDisciplines
                title="Students, Disciplines Year to Date"
                tooltip="Student year to date totals per disciplines"
                :disciplines="totals.total_students_ytd_discipline"
              />
            </div>
            <div class="flex-1">
              <div
                class="flex flex-wrap pt-3 pr-3 pl-3 pb-2 mt-3 mb-4 gap-3 border-2 border-gray-100 bg-white border-round-xl relative"
              >
                <div class="flex-1">
                  <div class="flex mb-2">
                    <Icon
                      name="calendar_today"
                      class="text-color-blue text-4xl p-2 bg-blue-light border-round align-self-start"
                    />
                    <div class="mx-3">
                      <h3 class="text-color-secondary text-base">Students, All Time</h3>
                      <p
                        :class="`text-color-blue-dark text-5xl font-bold`"
                        v-text="formatNumber(totals.total_students_all_time_minutes)"
                      />
                    </div>
                    <Icon
                      v-tooltip.bottom="`Students served all time for Sessions, Consultations, and Evaluations`"
                      name="info"
                      class="text-gray-400 text-base cursor-pointer align-self-start"
                    />
                  </div>
                </div>
                <div class="flex-1">
                  <div class="flex mb-2">
                    <Icon
                      name="assignment_turned_in"
                      class="text-color-orange text-4xl p-2 bg-orange-light border-round align-self-start"
                    />
                    <div class="mx-3">
                      <h3 class="text-color-secondary text-base">Students, All Time Minutes</h3>
                      <span
                        class="text-color-orange-dark text-5xl font-bold"
                        v-text="formatNumber(totals.total_students_minutes_all_time_minutes)"
                      />
                    </div>
                    <Icon
                      v-tooltip.bottom="`Student all time minutes for Sessions, Consultations, and Evaluations`"
                      name="info"
                      class="text-gray-400 text-base cursor-pointer align-self-start"
                    />
                  </div>
                </div>
              </div>
              <StudentTotalReportDisciplines
                title="Students, Disciplines All Time"
                tooltip="Student all time totals per disciplines"
                :disciplines="totals.total_students_all_time_discipline"
              />
            </div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  .p-datatable-header {
    background: $white;
    padding: 0.5rem;
    margin: 0rem -1rem;
  }

  .p-chart {
    height: 60px;
    width: 60px;

    &.attendance {
      height: 150px;
      width: 150px;
    }
  }

  .border-round {
    border-radius: 6px !important;
  }

  .border-round-top {
    border-radius: 4px 4px 0 0 !important;
  }

  .border-round-bottom {
    border-radius: 0 0 4px 4px !important;
  }
}

:deep(.p-calendar) {
  .pi.pi-calendar.p-button-icon {
    color: $white;
  }
}
</style>
